// Per-provider, per-model pricing.
//
// Numbers are USD per 1,000,000 tokens, sampled around 2026-04 from each
// provider's published pricing page. They drift over time; verify against
// the provider dashboard before relying on these for billing-critical
// decisions. The studio surfaces cost as a HINT to the user, not as an
// invoice — actual billing is whatever the provider charges them.

export interface ModelPricing {
  /** Cost per million input tokens. */
  inputPerM: number;
  /** Cost per million output tokens. */
  outputPerM: number;
  /** Cost per million cached/context-cached input tokens, if the provider exposes one. */
  cachedInputPerM?: number;
}

/**
 * Lookup table keyed by the same model id we send to the provider.
 * Models we don't recognize fall back to estimateUnknownPricing() which
 * returns a conservative-ish guess.
 */
export const PRICING: Record<string, ModelPricing> = {
  // --- Gemini ----------------------------------------------------------
  // https://ai.google.dev/pricing — paid tier (free tier exists for low
  // volume but rate-limited). Numbers are tiered (cheaper under 200K
  // tokens of context, more above); we use the cheaper tier as the
  // baseline because design-tool turns rarely exceed 200K input.
  'gemini-2.5-flash': { inputPerM: 0.3, outputPerM: 2.5 },
  'gemini-2.5-pro': { inputPerM: 1.25, outputPerM: 10.0 },
  'gemini-2.0-flash': { inputPerM: 0.1, outputPerM: 0.4 },
  'gemini-2.0-flash-exp': { inputPerM: 0.0, outputPerM: 0.0 },
  'gemini-2.0-flash-lite': { inputPerM: 0.075, outputPerM: 0.3 },

  // --- Minimax ---------------------------------------------------------
  // https://platform.minimaxi.com/document/Pricing — Chinese yuan
  // converted at ~7 RMB/USD; double-check exchange and current pricing
  // page before trusting these for finance.
  'MiniMax-M1': { inputPerM: 0.3, outputPerM: 1.65 },
  'MiniMax-Text-01': { inputPerM: 0.2, outputPerM: 1.1 },
  'abab6.5s-chat': { inputPerM: 1.0, outputPerM: 1.0 },
  'abab6.5-chat': { inputPerM: 4.0, outputPerM: 4.0 },
};

/**
 * Cost calculator. Returns USD as a finite number (always >= 0).
 * Unknown models log once and use a conservative fallback so we don't
 * silently misprice a turn.
 */
export function computeCostUsd(
  model: string,
  usage: { inputTokens: number; outputTokens: number; cachedInputTokens?: number },
): number {
  const pricing = PRICING[model] ?? estimateUnknownPricing(model);
  const inputCost = (usage.inputTokens / 1_000_000) * pricing.inputPerM;
  const outputCost = (usage.outputTokens / 1_000_000) * pricing.outputPerM;
  const cachedCost =
    pricing.cachedInputPerM != null && usage.cachedInputTokens != null
      ? (usage.cachedInputTokens / 1_000_000) * pricing.cachedInputPerM
      : 0;
  return Math.max(0, inputCost + outputCost + cachedCost);
}

const warnedUnknown = new Set<string>();

function estimateUnknownPricing(model: string): ModelPricing {
  if (!warnedUnknown.has(model)) {
    warnedUnknown.add(model);
    console.warn(
      `[cost] unknown model '${model}' — using fallback pricing. Add an entry to PRICING to fix.`,
    );
  }
  // Conservative-ish fallback: ~mid-tier per-M rates so the badge over-
  // estimates rather than under-estimates. Better to surprise the user
  // with "wait actually it cost less" than silent under-estimation.
  return { inputPerM: 1.5, outputPerM: 6.0 };
}

/** Format a USD number as a compact human-readable string. */
export function formatUsd(n: number): string {
  if (n < 0.001) return '<$0.001';
  if (n < 0.01) return `$${n.toFixed(4)}`;
  if (n < 1) return `$${n.toFixed(3)}`;
  return `$${n.toFixed(2)}`;
}
